/**
 * fabric-quantity.js
 *
 * Lets customers buy fabric in fractional units (metres / kg) on Shopify,
 * whose cart quantity is integer-only.
 *
 * How it works
 * ------------
 * The Shopify variant is priced per `step` of the selling unit (e.g. per 0.05 m).
 * The visible field shows the real quantity the customer wants (0.25, 1.50, 2.85);
 * a hidden `quantity` field carries the integer unit count Shopify needs
 * (5, 30, 57). divisor = 1 / step.
 *
 * All internal state is the integer unit count, so repeated +/- never drifts
 * the way floating point addition on 0.05 would.
 *
 * The running line total is mirrored into any [data-fq-line-total] element on
 * the page (rendered by snippets/price.liquid) so the money figure next to the
 * product title responds to the quantity. The headline price itself stays a
 * per-unit price - that is the convention shoppers expect - and the total sits
 * underneath it.
 *
 * A `properties[Quantity]` line item property carries the human-readable
 * quantity ("1.50 m") so it stays visible in cart, checkout, the order
 * confirmation email and the Shopify admin order screen.
 *
 * Failure mode note: this element only ever renders for fabric priced in
 * fractional units, so a step of 1 or more means the value did not arrive.
 * Falling back to 1 silently turns "1.5" into "2", so we fall back to
 * FALLBACK_STEP instead - always toward the intended behaviour.
 */
(function () {
  if (customElements.get('fabric-quantity')) return;

  const FALLBACK_STEP = 0.05;
  const FALLBACK_MIN = 0.25;

  class FabricQuantity extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      const d = this.dataset;

      this.uom = d.uom || '';
      this.step = this.toNum(d.step, FALLBACK_STEP);
      this.min = this.toNum(d.min, FALLBACK_MIN);
      this.max = d.max ? this.toNum(d.max, null) : null;
      this.baseCount = d.baseUnitCount ? this.toNum(d.baseUnitCount, null) : null;
      this.baseUnit = d.baseUnit || '';
      this.unitPriceCents = parseInt(d.unitPrice, 10) || 0;
      this.currency = d.currency || 'INR';

      // A whole-number step here is never intentional - treat it as missing.
      if (!(this.step > 0) || this.step >= 1) this.step = FALLBACK_STEP;
      if (!(this.min > 0)) this.min = FALLBACK_MIN;

      // 0.05 -> 20. Everything downstream is integer maths.
      this.divisor = Math.round(1 / this.step);
      this.decimals = (this.step.toFixed(10).replace(/0+$/, '').split('.')[1] || '').length;

      this.minUnits = Math.max(1, Math.round(this.min / this.step));
      this.maxUnits = this.max ? Math.max(this.minUnits, Math.round(this.max / this.step)) : null;

      // The +/- buttons move in coarser 0.5 steps regardless of the fine-grained
      // typing precision (`step`, e.g. 0.05) - two different granularities by design.
      this.buttonStep = 0.5;
      this.buttonUnits = Math.max(1, Math.round(this.buttonStep / this.step));

      this.display = this.querySelector('[data-fq-display]');
      this.unitsInput = this.querySelector('[data-fq-units]');
      this.propInput = this.querySelector('[data-fq-prop]');
      this.note = this.querySelector('[data-fq-note]');
      this.totalEl = this.querySelector('[data-fq-total]');
      this.errorEl = this.querySelector('[data-fq-error]');

      const minus = this.querySelector('[data-fq-minus]');
      const plus = this.querySelector('[data-fq-plus]');

      if (minus) {
        minus.addEventListener('click', (e) => {
          e.preventDefault();
          this.setUnits(this.units - this.buttonUnits);
        });
      }
      if (plus) {
        plus.addEventListener('click', (e) => {
          e.preventDefault();
          this.setUnits(this.units + this.buttonUnits);
        });
      }
      if (this.display) {
        // While typing we only update the readouts. Rewriting the visible field
        // mid-keystroke is what makes a field feel like it "won't accept" a value.
        this.display.addEventListener('input', () => this.previewTyped());
        this.display.addEventListener('blur', () => this.commitTyped());
        this.display.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.display.blur();
          }
        });
      }

      const seeded = this.display ? this.parseQty(this.display.value) : NaN;
      this.units = isNaN(seeded) ? this.minUnits : Math.round(seeded / this.step);
      this.setUnits(this.units, { silent: true });
    }

    toNum(v, fallback) {
      const n = parseFloat(v);
      return isNaN(n) ? fallback : n;
    }

    parseQty(v) {
      // Accept "1,5" as well as "1.5" - Indian keyboards produce both.
      return parseFloat(String(v == null ? '' : v).replace(',', '.').trim());
    }

    unitLabel() {
      return this.uom ? ' ' + this.uom : '';
    }

    clampUnits(u) {
      if (!Number.isFinite(u)) return this.minUnits;
      u = Math.round(u);
      if (u < this.minUnits) return this.minUnits;
      if (this.maxUnits != null && u > this.maxUnits) return this.maxUnits;
      return u;
    }

    /** Mirror the line total into the price area next to the product title. */
    syncPageTotals(cents) {
      const targets = document.querySelectorAll('[data-fq-line-total]');
      if (!targets.length) return;
      const text = this.money(cents);
      targets.forEach((el) => {
        el.textContent = text;
      });
      document.querySelectorAll('[data-fq-line-total-wrap]').forEach((el) => {
        el.removeAttribute('hidden');
      });
    }

    /** Everything that depends on the unit count, in one place. */
    paint(u, writeDisplay) {
      const qty = u * this.step;

      if (writeDisplay && this.display) this.display.value = this.fmtQty(qty);
      if (this.unitsInput) this.unitsInput.value = String(u);
      if (this.propInput) this.propInput.value = this.fmtQty(qty) + this.unitLabel();

      // The "approx. X kg" note (kg-equivalent of the metre quantity picked)
      // was confusing next to the per-kg price already shown above it, so
      // this note is intentionally never shown - the element stays hidden.
      if (this.note) this.note.setAttribute('hidden', '');

      if (this.unitPriceCents) {
        const cents = this.unitPriceCents * u;
        if (this.totalEl) this.totalEl.textContent = this.money(cents);
        this.syncPageTotals(cents);
      }

      return qty;
    }

    /**
     * A typed quantity is always rounded UP to the next multiple of `step`
     * (2.22 -> 2.25, never 2.20) - customers should never receive less fabric
     * than they asked for. The `- 1e-9` epsilon absorbs floating-point noise
     * (e.g. 2.25 / 0.05 landing on 45.00000000000001) so an already-exact
     * multiple doesn't get bumped up to the next one.
     */
    snapUnitsUp(raw) {
      const wanted = raw / this.step;
      return Math.ceil(wanted - 1e-9);
    }

    previewTyped() {
      const raw = this.parseQty(this.display.value);
      if (isNaN(raw)) return;
      this.paint(this.clampUnits(this.snapUnitsUp(raw)), false);
    }

    commitTyped() {
      const raw = this.parseQty(this.display.value);
      if (isNaN(raw)) {
        this.setUnits(this.units);
        return;
      }
      const wanted = raw / this.step;
      const snapped = this.snapUnitsUp(raw);
      const adjusted = Math.abs(wanted - snapped) > 1e-6;
      this.setUnits(snapped, { adjusted: adjusted });
    }

    setUnits(u, opts) {
      opts = opts || {};

      if (!Number.isFinite(u)) u = this.minUnits;
      u = Math.round(u);

      let clamped = false;
      if (u < this.minUnits) {
        u = this.minUnits;
        clamped = true;
      }
      if (this.maxUnits != null && u > this.maxUnits) {
        u = this.maxUnits;
        clamped = true;
      }

      this.units = u;
      const qty = this.paint(u, true);

      if (this.errorEl) {
        let msg = '';
        if (clamped && this.maxUnits != null && u === this.maxUnits) {
          msg = 'Maximum ' + this.fmtQty(this.maxUnits * this.step) + this.unitLabel() + ' per order.';
        } else if (clamped) {
          msg = 'Minimum ' + this.fmtQty(this.minUnits * this.step) + this.unitLabel() + ' per order.';
        } else if (opts.adjusted) {
          msg = 'Rounded up to the nearest ' + this.fmtQty(this.step) + this.unitLabel() + '.';
        }
        this.errorEl.textContent = msg;
        if (msg) this.errorEl.removeAttribute('hidden');
        else this.errorEl.setAttribute('hidden', '');
      }

      if (!opts.silent) {
        this.dispatchEvent(
          new CustomEvent('fabric-quantity:change', {
            bubbles: true,
            detail: { units: u, quantity: qty, uom: this.uom },
          })
        );
      }
    }

    fmtQty(v) {
      return v.toFixed(this.decimals);
    }

    money(cents) {
      const amount = cents / 100;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: 2,
        }).format(amount);
      } catch (e) {
        return amount.toFixed(2);
      }
    }
  }

  customElements.define('fabric-quantity', FabricQuantity);
})();
